<script setup>
import {ref, inject} from "vue";
import useGrid from "@/composables/useGrid";
import TablePager from "@/components/TablePager.vue";
import {trackerTypes, trackerTypesLabels} from "@/constants/trackers";
import AnswersView from "@/components/trackers/AnswersView.vue";
import trackersCompanies from "@/services/trackers-companies.service";

const emit = defineEmits(['addToTracker','trackerRemoved']);
const {onlyPublic = false} = defineProps(['onlyPublic', 'readOnly']);
const getTrackersIndexList = inject('getTrackersIndexList');
const getPublicTrackersIndexList = inject('getPublicTrackersIndexList', null);

const {gridParams, gridMethods, items, listLoaded} = useGrid(onlyPublic ? getPublicTrackersIndexList : getTrackersIndexList, 5);
const showModal = ref(false);
const viewAnswersModal = ref(null);
const companyType = inject('companyType', 'startup');
const companyId = inject('companyId', 0);
const companyName = inject('companyName', '');

function getTrackers() {
    gridMethods.updateList();
}

function addToTracker() {
    showModal.value = false;
    emit('addToTracker');
}

async function removeCompany(trackerId) {
    if (!confirm('Remove company from this tracker?')) {
        return;
    }

    await trackersCompanies.removeCompanies(trackerId, [companyId]);
    getTrackers();
    emit('trackerRemoved');
}

function open(reload = false) {
    if (reload) {
        gridMethods.resetList();
    } else {
        gridMethods.loadIfNotLoaded();
    }

    showModal.value = true;
}

function updateTrackers() {
    if (listLoaded.value) {
        getTrackers();
    }
}

defineExpose({open, updateTrackers});
</script>

<template>
    <div>
        <b-modal v-model="showModal" title="Trackers" hide-footer modal-class="modal-trackers modal-w-xxl">
            <a v-if="!onlyPublic && !readOnly" class="link link--add-data" @click="addToTracker">Add To A Tracker</a>

            <ul class="list-bordered">
                <li v-for="tracker in items" :key="tracker.id">
                    <div class="list-bordered__inner">
                        <header class="list-bordered__header">
                            <a class="mr-auto" :href="`/trackers/${tracker.id}`" target="_blank">{{tracker.name}}</a>

                            <div class="list-bordered__header__tags-list">
                                <div :class="'button-light button-light--' + companyType">{{tracker.companies_count}}</div>
                                <div v-if="tracker.type !== 'ecosystems'" :class="`button-light button-light--${tracker.type}`">{{trackerTypesLabels[tracker.type]}}</div>
                                <div v-if="tracker.stage" class="button-light button-light--tracker">{{tracker.stage}}</div>
                            </div>
                        </header>

                        <div class="list-bordered__description" v-html="tracker.description"></div>

                        <footer class="list-bordered__footer" v-if="tracker.has_submitted_answers">
                            {{companyName}} added answers to the startup fields in this tracker. 
                            <a class="link link--view" @click="viewAnswersModal.open(tracker.id, companyId)">View Answers</a>
                        </footer>
                    </div>

                    <div class="modal-manage__task__dropdown" 
                        v-if="tracker.user_can_delete_startups && tracker.type !== trackerTypes.PUBLIC && !readOnly"
                    >
                        <b-dropdown class="dropdown-original icons-list dropdown-small" boundary="viewport">
                            <b-dropdown-item class="icons-list__delete" @click="removeCompany(tracker.id)">
                                Remove
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </li>
            </ul>

            <TablePager
                v-if="items.length"
                :pages-count="gridParams.pagesCount"
                :init-page="gridParams.currentPage"
                @changePage="gridMethods.changePage"
            />
        </b-modal>

        <AnswersView ref="viewAnswersModal"/>
    </div>
</template>

<style scoped>

</style>
